<template>
    <sticky-cta-page :cta-props="ctaProps" :page-background-color="ctaProps.backgroundColor">
        <image-header-page class="image-header-page padding-bottom-xxl" :image-header-props="imageHeaderProps">
          <template v-slot:header>
            <div class="flex-column full-height full-width align-right header-container" >
              <div class="ri-body margin-top-sm flex-row align-center">
                <img :src="require('@/assets/icons/mortgages-flower.svg')" class="margin-bottom-xxs margin-left-xxs"/>
                המשכנתא של {{householdName}}
              </div>
              <div class="ri-medium-bold-display margin-top-l">
                נראה שכדאי למחזר את המשכנתא שלך
              </div>
              <div class="ri-large-body margin-top-m">
                המערכת האוטומטית של רייזאפ בוחנת כל יום מאות מסלולי משכנתא, ומצאה שאפשר לחסוך לך לא מעט כסף!          </div>
            </div>
          </template>
          <template v-slot:overlayContent>
            <div class="cards-container">
            <mortgage-alternative-card v-if="totalDiscountTotals" class="margin-bottom-m"
                                       title="חסכון בעלות הכוללת:" :img-src="require('@/assets/mortgage-total.svg')"
                                       saving-content="אפשר לחסוך על המשכנתא שלך כ-" :saving-amount="formatAmount(totalDiscountTotals.totalDiff)"
                                        :label-text="totalDiscountLabel"
                                        @click="openTotalSavingAlternative"/>
            <mortgage-alternative-card v-if="monthlyDiscountTotals" title="הוזלת ההחזר החודשי:"
                                       :img-src="require('@/assets/mortgage-monthly.svg')"
                                       saving-content="אפשר לצמצם את ההחזר החודשי שלכם בכ-"
                                       :saving-amount="formatAmount(monthlyDiscountTotals.monthlyDiff)"
                                       :label-text="monthlyDiscountLabel"
                                       @click="openMonthlySavingAlternative"
            />
            </div>
          </template>
        </image-header-page>
    </sticky-cta-page>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import Segment from '@/Segment';
import { mapActions, mapGetters } from 'vuex';
import MortgageAlternativeCard
  from '@/pages/responsive-pages/authenticated/navigation-pages/mortgages/MortgageAlternativeCard.vue';
import { RISEUP_BUTTON_COLORS } from '@riseupil/base-ui/src/components/RiseupButton/RiseupButton.consts';
import Intercom from '@/Intercom';
import moneyUtils from '@/utils/money';
import MortgageMenu, { MORTGAGE_MENU_VARIANTS } from '@/pages/responsive-pages/authenticated/navigation-pages/mortgages/MortgageMenu.vue';

export default {
  name: 'MortgageAlternativesPage',
  components: {
    MortgageAlternativeCard,
    ImageHeaderPage: BaseUI.Pages.ImageHeaderPage,
    StickyCtaPage: BaseUI.Pages.StickyCtaPage,
  },
  created() {
    Segment.trackUserInteraction('MortgagesAlternativesPage_Entered');
    this.setAppHeaderProps({
      backgroundColorVariant: BaseUI.LayoutConsts.COLOR_VARIANT.GREEN,
      textColor: BaseUI.Colors.riseupLightGreen,
      logoColor: BaseUI.Colors.riseupLightGreen,
    });
  },
  data() {
    return {
      imageHeaderProps: {
        backgroundColor: BaseUI.LayoutConsts.COLOR_VARIANT.GREEN,
        bottomSeparator: false,
        title: 'ההתקדמות\nשלי',
        textColor: BaseUI.Colors.riseupDarkGreen,
        backgroundPosition: BaseUI.LayoutConsts.BACKGROUND_POSITIONS.BOTTOM_LEFT,
        imgHeight: 314,
      },
    };
  },
  computed: {
    ...mapGetters('session', ['householdName']),
    ...mapGetters('mortgages', ['totalDiscountTotals', 'monthlyDiscountTotals', 'currentMortgageTotals']),
    ctaProps() {
      return {
        buttonProps: {
          loading: false,
          title: 'לייעוץ משכנתא עם רייזאפ',
          color: RISEUP_BUTTON_COLORS.GREEN,
          action: () => {
            Segment.trackUserInteraction('Mortgage_ContinueToAdvisoryClicked');
            this.openIntercomMessage();
          },
        },
        backgroundColor: BaseUI.Colors.riseupBeige,
      };
    },
    totalDiscountLabel() {
      const formattedMonthlyDiff = this.formatAmount(this.totalDiscountTotals.monthlyDiff);
      return this.totalDiscountTotals.monthlyDiff > 0 ? `גם ההחזר החודשי צפוי להצטמצם ב- ${formattedMonthlyDiff} ₪ `
        : 'ההחזר החודשי צפוי להשאר פחות או יותר זהה';
    },
    monthlyDiscountLabel() {
      const formattedTotalDiff = this.formatAmount(this.monthlyDiscountTotals.totalDiff);
      return this.monthlyDiscountTotals.totalDiff > 0 ? `גם העלות הכוללת צפויה להצטמצם ב- ${formattedTotalDiff} ₪ `
        : 'העלות הכוללת צפויה להשאר פחות או יותר זהה';
    },
  },
  methods: {
    ...mapActions('appHeader', ['setAppHeaderProps']),
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    openMonthlySavingAlternative() {
      Segment.trackUserInteraction('Mortgage_OpenMonthlySavingAlternativeClicked');
      this.openMenu({
        variant: MORTGAGE_MENU_VARIANTS.MONTHLY_SAVING,
        alternativeTotals: this.monthlyDiscountTotals,
        currentMortgageTotals: this.currentMortgageTotals,
        showNextOptionButton: !!this.totalDiscountTotals,
      });
    },
    openTotalSavingAlternative() {
      Segment.trackUserInteraction('Mortgage_OpenTotalSavingAlternativeClicked');
      this.openMenu({
        variant: MORTGAGE_MENU_VARIANTS.TOTAL_SAVING,
        alternativeTotals: this.totalDiscountTotals,
        currentMortgageTotals: this.currentMortgageTotals,
        showNextOptionButton: !!this.monthlyDiscountTotals,
      });
    },
    openMenu(menuProps) {
      this.openModal({
        component: MortgageMenu,
        props: menuProps,
        eventHandlers: {
          continue: () => {
            this.closeModal();
            Segment.trackUserInteraction('Mortgage_ContinueToAdvisoryFromMortgageDetails_Clicked');
            this.openIntercomMessage();
          },
          next: () => {
            Segment.trackUserInteraction('Mortgage_ShowNextAlternativeClicked');
            this.closeModal();
            if (menuProps.variant === MORTGAGE_MENU_VARIANTS.TOTAL_SAVING) {
              this.openMonthlySavingAlternative();
            } else {
              this.openTotalSavingAlternative();
            }
          },
          close: () => {
            Segment.trackUserInteraction('Mortgage_ShowNextAlternativeClicked');
            this.closeModal();
          },
        },
        popupAlignment: 'menu',
      });
    },
    formatAmount(amount) {
      return moneyUtils.signedFormatAmountWithFraction(amount, 0);
    },
    openIntercomMessage() {
      Intercom.showNewMessage('מעניין אותי לשמוע עוד על מיחזור המשכנתא שלי דרך רייזאפ');
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/spacings";

.header-container{
  text-align: right;
}

.image-header-page {
  background: $riseup_beige;
}

</style>
